import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useMemo, useState } from 'react';
import { Donation } from '../../../api/models/Donation';
import { apiConfigs } from '../../../api/APIConfigs';
import { useUserRoles } from '../../../util/use-user-role';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { DonationsScreen } from '../components/DonationsScreen';

export type DonationsProps = {
  // Inputs
  /** list of donations */
  donations: Donation[];
  /** donations grouped by church (for System Admin) */
  donationsByChurch: { [churchId: string]: { churchName: string; donations: Donation[]; totalAmount: number } };
  /** filtered churches for System Admin church list */
  filteredChurches: Array<{ churchId: string; churchName: string; totalAmount: number; totalDonations: number }>;
  /** selected church ID for System Admin detail view */
  selectedChurchId: string | null;
  /** paginated donations for selected church or Church Admin view */
  paginatedDonations: Donation[];
  /** current page number */
  currentPage: number;
  /** total number of pages */
  totalPages: number;
  /** items per page */
  itemsPerPage: number;
  /** whether or not list of donations is fetching */
  isLoadingDonations: boolean;
  /** search term for filtering */
  searchTerm: string;
  /** status filter: 'all', 'completed', 'processing', 'failed' */
  statusFilter: string;
  /** from date for date range filter */
  fromDate: Date | null;
  /** to date for date range filter */
  toDate: Date | null;
  /** current user role */
  userRole: string | undefined;
  /** whether user is System Admin */
  isSystemAdmin: boolean;

  // Outputs
  /** called when the search field receives a [change] event */
  userUpdatedSearchTerm: (term: string) => void;
  /** called when status filter changes */
  userUpdatedStatusFilter: (status: string) => void;
  /** called when from date changes */
  userUpdatedFromDate: (date: Date | null) => void;
  /** called when to date changes */
  userUpdatedToDate: (date: Date | null) => void;
  /** called when a church row is clicked (System Admin) */
  userClickedChurch: (churchId: string) => void;
  /** called when user clicks back from church detail view */
  userClickedBack: () => void;
  /** called when user changes page */
  userChangedPage: (page: number) => void;
};

export const DonationsContainer = () => {
  const { roles } = useUserRoles();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Set default date range to last 30 days
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  };

  const [fromDate, setFromDate] = useState<Date | null>(getDefaultFromDate());
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [selectedChurchId, setSelectedChurchId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  const userRole = roles?.[0];
  const isSystemAdmin = userRole === 'System Admin';

  const request = useRequest();
  const { getIdTokenClaims, user } = useAuth0();

  const [churchId, setChurchId] = useState<string | undefined>(undefined);
  const [churchSearchQuery, setChurchSearchQuery] = useState<string>('');
  const [emailSearchQuery, setEmailSearchQuery] = useState<string>('');

  React.useEffect(() => {
    (async () => {
      if (isSystemAdmin) return;
      if (churchId) return;

      const userObject: any = user ? JSON.parse(JSON.stringify(user)) : undefined;
      const directChurchId = userObject?.['https:///churchid'];
      if (directChurchId) {
        setChurchId(directChurchId);
        return;
      }

      try {
        const claims = await getIdTokenClaims();
        let audience = process.env.REACT_APP_AUTH0_AUDIENCE;
        if (!audience?.endsWith('/api')) {
          if (audience?.endsWith('/')) audience = audience.slice(0, -1);
          audience = (audience || 'https://risenhope.appwrk.com') + '/api';
        }
        const claimKey = `${audience}/churchid`;
        const extracted = (claims as any)?.[claimKey];
        if (extracted) {
          setChurchId(extracted);
        }
      } catch (e) {
        // If we can't resolve churchId, Church Admin view will remain empty until backend provides scoping
        console.error('Failed to get churchId from Auth0', e);
      }
    })();
  }, [getIdTokenClaims, user, isSystemAdmin, churchId]);

  // Debounce church name search for System Admin church list
  React.useEffect(() => {
    if (!isSystemAdmin || selectedChurchId) return;

    const handle = setTimeout(() => {
      setChurchSearchQuery(searchTerm.trim());
    }, 300);

    return () => clearTimeout(handle);
  }, [searchTerm, isSystemAdmin, selectedChurchId]);

  // Debounce email search for donations view (when church is selected or Church Admin)
  React.useEffect(() => {
    // Only debounce email search when viewing donations (not church list)
    const isViewingDonations = (isSystemAdmin && selectedChurchId) || (!isSystemAdmin && churchId);
    if (!isViewingDonations) return;

    const handle = setTimeout(() => {
      setEmailSearchQuery(searchTerm.trim());
    }, 300);

    return () => clearTimeout(handle);
  }, [searchTerm, isSystemAdmin, selectedChurchId, churchId]);

  const [paymentsSummary, setPaymentsSummary] = useState<
    Array<{ churchId: string; churchName: string; totalAmount: number; totalDonations: number }>
  >([]);

  const { loading: isLoadingPaymentsSummary } = useAsyncRequest<any[]>(
    useCallback(() => request<any[]>(apiConfigs.getPaymentsSummary(churchSearchQuery || undefined)), [
      request,
      churchSearchQuery,
    ]),
    useCallback(result => {
      const mapped =
        result?.map((item: any) => ({
          churchId: item.churchId || item.id || item.churchID || '',
          churchName: item.churchName || item.churchKey || 'Unknown Church',
          totalAmount: Number(item.totalAmount ?? item.amount ?? 0),
          totalDonations: Number(item.totalDonations ?? 0),
        })) || [];

      setPaymentsSummary(mapped);
    }, [])
  );

  const [churchPayments, setChurchPayments] = useState<Donation[]>([]);

  // Format date as YYYY-MM-DD for API
  const formatDateForAPI = (date: Date | null): string | undefined => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Map frontend status filter to backend status
  const getBackendStatus = (statusFilter: string): string | undefined => {
    if (statusFilter === 'all') return undefined;
    if (statusFilter === 'completed') return 'succeeded';
    if (statusFilter === 'processing') return 'processing';
    if (statusFilter === 'failed') return 'failed';
    return undefined;
  };

  const { loading: isLoadingChurchPayments } = useAsyncRequest<any[]>(
    useCallback(() => {
      const effectiveChurchId = isSystemAdmin ? selectedChurchId : churchId;
      if (!effectiveChurchId) {
        return Promise.resolve<any[]>([]);
      }
      return request<any[]>(
        apiConfigs.getPaymentsByChurch(effectiveChurchId, {
          dateFrom: formatDateForAPI(fromDate),
          dateTo: formatDateForAPI(toDate),
          statuses: getBackendStatus(statusFilter),
          emailId: emailSearchQuery || undefined,
        })
      );
    }, [request, isSystemAdmin, selectedChurchId, churchId, fromDate, toDate, statusFilter, emailSearchQuery]),
    useCallback(
      result => {
        const effectiveChurchId = isSystemAdmin ? selectedChurchId : churchId;
        if (!effectiveChurchId) {
          setChurchPayments([]);
          return;
        }

        const mapped: Donation[] =
          result?.map((item: any) => {
            const normalizedStatus =
              (item.status || '').toLowerCase() === 'succeeded' ? 'completed' : item.status || 'processing';

            return {
              id: item.stripeId || item.id || item.paymentId || '',
              userId: item.userId || '',
              userName: item.userName || '',
              emailId: item.emailId || '',
              churchId: item.churchId || effectiveChurchId,
              churchName: item.churchName || '',
              stripeId: item.stripeId,
              amount: Number(item.amount ?? 0),
              date: new Date(item.receivedAt || item.date || item.created || new Date()),
              status: normalizedStatus,
              paymentMethod: item.paymentMethod,
              notes: item.notes,
              created: new Date(item.created || item.receivedAt || item.date || new Date()),
            };
          }) || [];

        setChurchPayments(mapped);
      },
      [isSystemAdmin, selectedChurchId, churchId]
    )
  );

  const isLoadingDonations = isSystemAdmin
    ? isLoadingPaymentsSummary || isLoadingChurchPayments
    : isLoadingChurchPayments;

  const donationsByChurch = useMemo(() => {
    if (!isSystemAdmin) return {};
    if (!selectedChurchId) return {};

    const summary = paymentsSummary.find(s => s.churchId === selectedChurchId);
    const totalAmount =
      typeof summary?.totalAmount === 'number'
        ? summary.totalAmount
        : churchPayments.reduce((sum, d) => sum + (d.amount || 0), 0);

    return {
      [selectedChurchId]: {
        churchName: summary?.churchName || 'Unknown Church',
        donations: churchPayments,
        totalAmount,
      },
    };
  }, [isSystemAdmin, selectedChurchId, paymentsSummary, churchPayments]);

  // Backend handles all filtering (status, date, email), so we just use the API result directly
  const searchedDonations = useMemo(() => {
    return churchPayments;
  }, [churchPayments]);

  const filteredChurches = useMemo(() => {
    if (!isSystemAdmin || selectedChurchId) return [];
    return paymentsSummary;
  }, [isSystemAdmin, selectedChurchId, paymentsSummary]);

  // Get donations to display based on view
  const donationsToDisplay = useMemo(() => {
    return searchedDonations;
  }, [searchedDonations]);

  // Pagination logic
  const totalPages = Math.ceil(donationsToDisplay.length / itemsPerPage);
  const paginatedDonations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return donationsToDisplay.slice(startIndex, endIndex);
  }, [donationsToDisplay, currentPage, itemsPerPage]);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedChurchId, searchTerm, statusFilter, fromDate, toDate]);

  return (
    <DonationsScreen
      donations={searchedDonations}
      donationsByChurch={donationsByChurch}
      filteredChurches={filteredChurches}
      selectedChurchId={selectedChurchId}
      paginatedDonations={paginatedDonations}
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      isLoadingDonations={isLoadingDonations}
      searchTerm={searchTerm}
      statusFilter={statusFilter}
      fromDate={fromDate}
      toDate={toDate}
      userRole={userRole}
      isSystemAdmin={isSystemAdmin}
      userUpdatedSearchTerm={setSearchTerm}
      userUpdatedStatusFilter={setStatusFilter}
      userUpdatedFromDate={setFromDate}
      userUpdatedToDate={setToDate}
      userClickedChurch={setSelectedChurchId}
      userClickedBack={() => setSelectedChurchId(null)}
      userChangedPage={setCurrentPage}
    />
  );
};
